import { isMidExpired, isCustomSection } from '@/helper/irs-clob-grid.helper';

// What a CLOB Mid cell shows: a live session, a session that ended within the window, or Deon's
// System mid for the structure. Live outranks Ended outranks System.
export const MID_STATE = { LIVE: 'live', ENDED: 'ended', SYSTEM: 'system' };

// A session ended by a person carries endedAt; one that ran out of time ends at its endsAt.
const endedAtOf = (entry) => entry.endedAt ?? entry.endsAt ?? null;

export const isEndedShowing = (entry, serverTime, endedWindowMs) => {
    const endedAt = endedAtOf(entry);
    if (!endedAt || !endedWindowMs) return false;
    const now = new Date(serverTime || Date.now()).getTime();
    return new Date(endedAt).getTime() + endedWindowMs > now;
};

// The Mid rows of one structure, in display order: live sessions, then sessions that ended within
// the window. With neither, one System row when Deon has a System mid for it — on the first row only.
// Under a live-only view only live sessions show: System and Ended mids are not live prices.
export const resolveMidRows = ({ midStack = [], systemMid = null, serverTime, endedWindowMs, liveOnly = false }) => {
    const live = midStack
        .filter((entry) => !isMidExpired(entry, serverTime))
        .map((entry) => ({ ...entry, midState: MID_STATE.LIVE }));
    if (liveOnly) return live;
    const ended = midStack
        .filter((entry) => isMidExpired(entry, serverTime) && isEndedShowing(entry, serverTime, endedWindowMs))
        .map((entry) => ({ ...entry, midState: MID_STATE.ENDED }));
    if (live.length || ended.length) return [...live, ...ended];
    // '' = the form has no price for this structure: the cell stays empty.
    return systemMid == null || systemMid === '' ? [] : [{ midState: MID_STATE.SYSTEM, mid: systemMid }];
};

// Only a live session's mid is a price: System and Ended mids are never live, never a match.
const isPrice = (value) => value != null && value !== '' && Number.isFinite(Number(value));

// The off-mid reference for a CLOB row: its System mid, or null when it has none. The row's own
// `.mid` is a session mid and is never a reference.
export const systemMidReferenceOf = (raw) => (isPrice(raw?.systemMid) ? Number(raw.systemMid) : null);

// 'up' / 'down' per structure whose System mid moved since `previous` (structure id → System mid).
// A System mid appearing or going away is not a move.
export const systemMidMoves = (securities, previous) => Object.fromEntries(securities
    .map((security) => [security.id, previous.get(security.id), security?.raw?.systemMid])
    .filter(([, before, after]) => isPrice(before) && isPrice(after) && Number(after) !== Number(before))
    .map(([id, before, after]) => [id, Number(after) > Number(before) ? 'up' : 'down']));

// The System mid as the grid shows it: 3 decimals for an outright (meeting-date outrights included),
// 1 for a spread, butterfly or custom structure. Launch Mid Match keeps the full-precision value.
export const formatSystemMid = (value, raw, sectionType) => {
    if (!isPrice(value)) return value;
    const isSpreadOrFly = isCustomSection(sectionType) || Boolean(raw?.start1Label || raw?.end1Label || raw?.end2Label);
    return Number(value).toFixed(isSpreadOrFly ? 1 : 3);
};

export const isLiveMidState = (midState) => (midState ?? MID_STATE.LIVE) === MID_STATE.LIVE;

// "4h 05m" / "12m" — how long a session ran, from its start to when it ended.
export const formatRunTime = (startedAt, endedAt) => {
    if (!startedAt || !endedAt) return null;
    const minutes = Math.max(0, Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 60000));
    const hours = Math.floor(minutes / 60);
    return hours ? `${hours}h ${String(minutes % 60).padStart(2, '0')}m` : `${minutes}m`;
};

// What the Ended cell's hover tells: when the session ended, how long ago, how long it ran, whose it was.
export const endedMidDetails = ({ endedAt, startedAt, broker }, serverTime) => {
    if (!endedAt) return null;
    const now = new Date(serverTime || Date.now()).getTime();
    return {
        endedAtTime: new Date(endedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
        minutesAgo: Math.max(0, Math.floor((now - new Date(endedAt).getTime()) / 60000)),
        ranFor: formatRunTime(startedAt, endedAt),
        broker: broker || null
    };
};
